/**
 * @file reviewStep.component.tsx
 * @path apps/website/src/features/booking/components/steps/reviewStep.component.tsx
 */

import { CompletedBooking } from "@sirbastians/types";
import { Button, Card, CardContent, Separator } from "@sirbastians/ui";
import { formatCurrency } from "@sirbastians/utils";
import { format } from "date-fns";
import {
  ArrowRightLeft,
  CalendarPlus,
  Edit2,
  Loader2,
  Receipt,
  Trash2,
} from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import {
  selectIsItineraryLocked,
  selectShowReturnTrip,
  useBookingStore,
} from "../booking.store";
import { useBookingPricing } from "../hooks/useBookingPricing.hook";
import { useYocoCheckout } from "../hooks/useYocoCheckout.hook";
import { calculateTotalForAllBookings } from "../pricing.util";

interface ReviewStepProps {
  onGoToStep: (stepId: string) => void;
}

export const ReviewStep = ({ onGoToStep }: ReviewStepProps) => {
  const store = useBookingStore();
  const { pricing } = useBookingPricing();
  const { handlePayNow, isProcessing } = useYocoCheckout();

  const showReturnTrip = useBookingStore(selectShowReturnTrip);
  const isLocked = useBookingStore(selectIsItineraryLocked);

  const {
    selectedService,
    selectedDate,
    selectedTime,
    completedBookings,
    deleteBooking,
    loadForEdit,
    resetForNewBooking,
    addReturnTrip,
  } = store;

  // Build draft booking preview
  const draftBooking: CompletedBooking | null = useMemo(() => {
    if (!selectedService || !selectedDate || !selectedTime) return null;
    return {
      id: "draft-preview",
      bookingDate: selectedDate,
      selectedService,
      includedActivities: store.includedActivities,
      selectedActivities: store.selectedActivities,
      passengers: store.passengers,
      adultCount: store.adultsCount,
      childCount: store.childrenCount,
      infantCount: store.infantsCount,
      luggageCount: store.luggageCount,
      babySeats: store.babySeats,
      selectedTime,
      duration: store.duration,
      selectedVehicles: store.selectedVehicles,
      routeStops: store.routeStops,
      trailerNeeded: store.trailerNeeded,
      flightNumber: store.flightNumber,
      pricing: {
        service: pricing.servicePrice,
        activities: pricing.activitiesPrice,
        addOns:
          pricing.addOnsPrice + pricing.vehiclesPrice + pricing.extraStopsPrice,
        total: pricing.total,
      },
    };
  }, [store, pricing, selectedService, selectedDate, selectedTime]);

  const allBookings = useMemo(() => {
    const list = [...completedBookings];
    if (draftBooking) list.push(draftBooking);
    return list;
  }, [completedBookings, draftBooking]);

  const grandTotal =
    calculateTotalForAllBookings(completedBookings) +
    (draftBooking?.pricing.total ?? 0);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleAddDay = () => {
    resetForNewBooking();
    toast.success("Booking saved! Select a service for your next trip.");
    onGoToStep("service");
  };

  const handleReturnTrip = () => {
    addReturnTrip();
    toast.success(
      "Return trip started! We've reversed your route and kept your details.",
    );
    onGoToStep("trip-details");
  };

  const handleEdit = (id: string) => {
    if (id === "draft-preview") {
      onGoToStep("service");
    } else {
      loadForEdit(id);
      onGoToStep("service");
    }
  };

  const handleDelete = (id: string) => {
    if (id === "draft-preview") {
      resetForNewBooking();
      onGoToStep("service");
    } else {
      deleteBooking(id);
      toast.info("Booking removed from itinerary.");
    }
  };

  if (allBookings.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="text-muted-foreground">No bookings yet.</p>
        <Button variant="outline" onClick={() => onGoToStep("service")}>
          Start a Booking
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-end px-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Booking Receipt</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Review your detailed itinerary below.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground font-medium">
            Grand Total
          </p>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-accent tracking-tight">
            {formatCurrency(grandTotal)}
          </h3>
        </div>
      </div>

      {/* Booking cards */}
      <div className="grid gap-6">
        {allBookings.map((booking, idx) => {
          const isDraft = booking.id === "draft-preview";
          return (
            <BookingCard
              key={booking.id}
              booking={booking}
              index={idx}
              isDraft={isDraft}
              onEdit={() => handleEdit(booking.id)}
              onDelete={() => handleDelete(booking.id)}
            />
          );
        })}
      </div>

      {/* Action row */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
        {showReturnTrip && (
          <Button
            variant="outline"
            className="gap-2 border-accent text-accent hover:bg-accent/5"
            onClick={handleReturnTrip}
          >
            <ArrowRightLeft className="h-4 w-4" /> Add Return Trip
          </Button>
        )}

        {!showReturnTrip && !isLocked && (
          <Button variant="outline" className="gap-2" onClick={handleAddDay}>
            <CalendarPlus className="h-4 w-4" /> Add Another Day
          </Button>
        )}

        <Button
          onClick={handlePayNow}
          disabled={isProcessing}
          className="gap-2 min-w-[160px]"
        >
          {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
          {isProcessing ? "Processing…" : "Pay Now"}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BookingCard
// ---------------------------------------------------------------------------

interface BookingCardProps {
  booking: CompletedBooking;
  index: number;
  isDraft: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const BookingCard = ({
  booking,
  index,
  isDraft,
  onEdit,
  onDelete,
}: BookingCardProps) => (
  <Card
    className={`group transition-all duration-200 overflow-hidden ${
      isDraft ? "border-accent ring-1 ring-accent/20" : "hover:border-accent/50"
    }`}
  >
    {/* Top colour strip */}
    <div className={`h-1.5 w-full ${isDraft ? "bg-accent" : "bg-muted"}`} />

    <CardContent className="p-6">
      {/* Header row */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center h-8 w-8 rounded-full bg-muted text-muted-foreground font-bold text-sm border">
            {index + 1}
          </span>
          <div>
            <h3 className="font-bold text-lg leading-none">
              {booking.selectedService.title}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {format(booking.bookingDate, "EEEE, dd MMM yyyy")} at{" "}
              {booking.selectedTime}
            </p>
          </div>
        </div>

        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-accent"
            onClick={onEdit}
            title="Edit booking"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={onDelete}
            title="Remove booking"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Pricing breakdown */}
      <div className="bg-muted/30 rounded-lg p-4 space-y-3 border border-border/50">
        <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Receipt className="h-3 w-3" /> Charges Breakdown
        </div>

        <LineItem label="Base Service Fare" value={booking.pricing.service} />

        {booking.pricing.activities > 0 && (
          <LineItem
            label={`Activities (${booking.selectedActivities.length})`}
            value={booking.pricing.activities}
          />
        )}

        {booking.pricing.addOns > 0 && (
          <LineItem label="Add-ons & Extras" value={booking.pricing.addOns} />
        )}

        <Separator className="my-2" />

        <LineItem label="Booking Total" value={booking.pricing.total} isTotal />
      </div>
    </CardContent>
  </Card>
);

const LineItem = ({
  label,
  value,
  isTotal = false,
}: {
  label: string;
  value: number;
  isTotal?: boolean;
}) => (
  <div
    className={`flex justify-between text-sm ${
      isTotal ? "font-bold text-foreground mt-2" : "text-muted-foreground"
    }`}
  >
    <span>{label}</span>
    <span>{formatCurrency(value)}</span>
  </div>
);
